/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { InventoryAvailabilitySearchCriteria, ProductWarehouseInventoryInformation, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import { getInventoryLabel } from '../../mil-search-result-container/components';
import { IImageSettings } from '@msdyn365-commerce/core-internal';
import { MilProductComponent } from './mil-product.component';
import { IUnitOfMeasureConversionResponseMapEntity } from '../../../actions/DataServiceEntities.g';
import { IMillProductsListPageResources } from '../mill-products-list-page.props.autogenerated';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

export interface IMilSearchResultsItems {
    products?: SimpleProduct[] | undefined;
    context: any;
    telemetryContent?: ITelemetryContent;
    id: string;
    typeName: string;
    imageSettings?: IImageSettings;
    uomData: IUnitOfMeasureConversionResponseMapEntity[] | undefined;
    resources: IMillProductsListPageResources;
    allowBackNavigation?: boolean;
    quickviewSlot?: {} | null | undefined;
    productComparisonButton?: {} | null | undefined;
    showListView: boolean;
}

export const MilSearchResultsItems: React.FC<IMilSearchResultsItems> = props => {
    const {
        products = [],
        context,
        telemetryContent,
        id,
        typeName,
        imageSettings,
        uomData,
        resources,
        quickviewSlot,
        productComparisonButton,
        showListView
    } = props;

    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    const [estimatedAvailabilites, setEstimatedAvailabilites] = React.useState<ProductWarehouseInventoryInformation | undefined>();

    const getEsimatedAvailability = async (): Promise<void> => {
        const itemIds = Array.from(new Set(products.map((product: SimpleProduct) => product.RecordId)));
        const inventSearch: InventoryAvailabilitySearchCriteria = {
            ProductIds: itemIds,
            DefaultWarehouseOnly: false,
            FilterByChannelFulfillmentGroup: true
        };

        const availabilities = await getEstimatedAvailabilityAsync({ callerContext: context.actionContext }, inventSearch);
        setEstimatedAvailabilites(availabilities);
    };

    React.useEffect(() => {
        getEsimatedAvailability();
    }, [products]);

    if (!products || products.length === 0) {
        return null;
    }

    return (
        <ul className={showListView ? 'mil-list-view' : 'mil-grid-view'}>
            {products.map((product, idx) => {
                const key = product?.RecordId ?? idx;
                const productUom = uomData?.find(uom => uom.ItemRecId === product.RecordId);
                const productBehavior = product.Behavior;
                const availInvs = estimatedAvailabilites?.AggregatedProductInventoryAvailabilities?.find(
                    inv => inv.ProductId === product.RecordId
                );
                return (
                    <li className='ms-product-search-result__item' key={key}>
                        <MilProductComponent
                            context={context}
                            telemetryContent={telemetryContent}
                            imageSettings={imageSettings}
                            freePriceText={resources.priceFree}
                            originalPriceText={resources.originalPriceText}
                            currentPriceText={resources.currentPriceText}
                            shouldDisplayPrice={!!products}
                            ratingAriaLabel={resources.ratingAriaLabel}
                            allowBack={true}
                            id={id}
                            key={product.RecordId}
                            typeName={typeName}
                            data={{ product }}
                            quickViewButton={quickviewSlot}
                            productComparisonButton={productComparisonButton}
                            inventoryLabel={getInventoryLabel(3453451345, product)}
                            isPriceMinMaxEnabled={true}
                            priceResources={priceResources}
                            swatchItemAriaLabel={resources.swatchItemAriaLabel}
                            desiredNonstockCommerceName={context.app.config.nonstockCommerceState}
                            nonstockCommerceState={context.app.config.desiredNonstockCommerceName}
                            stockStatusText={resources.stockStatusText}
                            productUom={productUom}
                            showListView={showListView}
                            minimumOrderQtyLabelText={resources.minimumOrderQtyLabelText}
                            productBehavior={productBehavior}
                            availInvs={availInvs}
                            availableStockLabel={resources.availableStockLabel}
                        />
                    </li>
                );
            })}
        </ul>
    );
};

export default MilSearchResultsItems;
